import { SYMBOLCODE_TO_IMAGES } from "@/geojson";
import {
  POI_BOUNDARY_STATE_PROPERTY,
  POI_INVALID_VALUE_STATE,
  POI_OUT_OF_BOUNDS_COLOR,
  POI_OUT_OF_BOUNDS_OPACITY_MULTIPLIER,
  POI_OUT_OF_BOUNDS_STATE,
} from "@/layers/geojson/poi/poiBoundary";
import { advancedThresholdColorExpression } from "@/layers/utility/advancedThresholdColorExpression";
import { MapboxLayerGroup } from "@/layers/utility/mapbox-utils";
import { thresholdColorExpression } from "@/layers/utility/thresholdColorExpression";
import type { PoiLayer } from "@/reducer/client-models/PoiLayer";
import { WEATHER_SYMBOL_PARAM } from "@/weatherTableView/cellsRenderers/DataCellRenderer";
import { tryPrettyPrintUnit } from "@mm/weather-parameter-utils";
import type { Expression, SymbolLayout, SymbolPaint } from "mapbox-gl";
import { TEXT_BACKGROUND_IMG } from "./poi-mapbox-image/poiMapboxImage";

/**
 * Matches the internal PoI boundary state used by the cache/render pipeline for grey fallback styling.
 */
function getGreyedOutStateExpression(): Expression {
  return ["in", ["get", POI_BOUNDARY_STATE_PROPERTY], ["literal", [POI_OUT_OF_BOUNDS_STATE, POI_INVALID_VALUE_STATE]]];
}

/**
 * Overrides threshold-driven colors with the neutral fallback color for out-of-bounds and invalid-value points.
 */
function getOutOfBoundsColorExpression(color: Expression | string): Expression {
  return ["case", getGreyedOutStateExpression(), POI_OUT_OF_BOUNDS_COLOR, color];
}

/**
 * Applies the reduced opacity multiplier used for greyed-out PoIs.
 */
function getOutOfBoundsOpacityExpression(opacity: number): Expression {
  return ["case", getGreyedOutStateExpression(), opacity * POI_OUT_OF_BOUNDS_OPACITY_MULTIPLIER, opacity];
}

/**
 * Keeps value pills hidden when the cache intentionally removed the value from an out-of-bounds or invalid point.
 */
function hideWhenValueMissingExpression(opacity: number): Expression {
  return ["case", ["==", ["get", "value"], null], 0, getOutOfBoundsOpacityExpression(opacity)];
}

/**
 * Wraps the normal threshold color expression with the branch-specific grey fallback styling.
 */
function getColorExpression(props: PoiLayer): Expression {
  const advancedThresholds = props.poiOptions.coloringOptions?.advancedThresholds || [];
  const thresholds = props.poiOptions.coloringOptions?.thresholds || [];
  const baseColor =
    advancedThresholds.length > 0
      ? advancedThresholdColorExpression(props.poiOptions.item.color, advancedThresholds)
      : thresholdColorExpression(props.poiOptions.item.color, thresholds);

  return getOutOfBoundsColorExpression(baseColor);
}

export const iconLayerLayoutProperties = (props: PoiLayer): SymbolLayout => ({
  visibility: props.show ? "visible" : "none",
  "icon-image": ["get", "item-icon"],
  "icon-size": ["get", "item-icon-size"],
  "text-allow-overlap": false,
  "text-ignore-placement": false,
  "icon-allow-overlap": true,
  "icon-ignore-placement": true,
  "text-field": ["get", "item-text"],
  "text-size": ["get", "item-text-size"],
});

export const iconLayerPaintProperties = (props: PoiLayer): SymbolPaint => {
  return {
    "text-opacity": getOutOfBoundsOpacityExpression(props.opacity),
    "icon-opacity": getOutOfBoundsOpacityExpression(props.opacity),
    "icon-color": getColorExpression(props),
    "text-color": getColorExpression(props),
  };
};

export const createWeatherSymbolExpression = (): Expression => {
  // Start the match expression with the 'get' command
  const matchExpression: Expression = ["match", ["get", "value"]];
  for (const [key, value] of Object.entries(SYMBOLCODE_TO_IMAGES)) {
    matchExpression.push(Number(key), value);
  }
  // Add a default case in case no keys match
  matchExpression.push("wsymbol_0999_unknown");
  return matchExpression;
};
const DEFAULT_VALUE_LAYER_LAYOUT_PROPERTIES: SymbolLayout = {
  "icon-ignore-placement": true,
  "icon-allow-overlap": true,
  "icon-text-fit": "both",
  "icon-text-fit-padding": [2, 10, 2, 10],

  "text-ignore-placement": false,
  "text-allow-overlap": false,
};
export const valueLayerLayoutProperties = (props: PoiLayer): SymbolLayout => {
  const isWeatherSymbol = props.parameter_unit?.startsWith(WEATHER_SYMBOL_PARAM);

  const iconImage: string | Expression | undefined = isWeatherSymbol
    ? createWeatherSymbolExpression()
    : TEXT_BACKGROUND_IMG;

  const layoutProperties: SymbolLayout = isWeatherSymbol
    ? {
        ...DEFAULT_VALUE_LAYER_LAYOUT_PROPERTIES,
        "icon-size": ["get", "item-icon-size"],
        "symbol-placement": "point",
      }
    : DEFAULT_VALUE_LAYER_LAYOUT_PROPERTIES;

  return {
    "icon-image": iconImage,
    ...layoutProperties,
    ...unitLabelLayout(props),
    ...valueLayerOffset(props),
  };
};

/**
 * Value pills follow the same grey fallback rules as icon/text markers, but remain fully hidden when the
 * rendered feature has no `value` property.
 */
export const valueLayerPaintProperties = (props: PoiLayer): SymbolPaint => {
  const labelBackgroundOpacity = props.opacity * (props.poiOptions.item.labelBackgroundOpacity ?? 1);

  return {
    "icon-opacity": hideWhenValueMissingExpression(labelBackgroundOpacity),
    "text-opacity": hideWhenValueMissingExpression(props.opacity),
    "icon-color": getColorExpression(props),
    "text-color": getColorExpression(props),
  };
};

/**
 * Creates initial mapbox layer specifications for POI layer.
 * @param instanceId
 * @param props
 * @returns
 */
export function poiMapboxLayerGroup(
  instanceId: string,
  props: PoiLayer,
  // TODO: Maybe change this to SourceTracker?
  sourceName: string,
) {
  const group = new MapboxLayerGroup(instanceId, [
    {
      // Main POI layer to show the marker icons and texts
      layerName: "icon-layer",
      type: "symbol",
      source: sourceName,
      paint: iconLayerPaintProperties(props),
      layout: iconLayerLayoutProperties(props),
    },
    {
      // Sub layer to show the value pills when a user adds a parameter.
      // When the parameter is weather_symbol* it shows the weather symbol only
      layerName: "value-layer",
      type: "symbol",
      source: sourceName,
      paint: valueLayerPaintProperties(props),
      layout: valueLayerLayoutProperties(props),
    },
  ]);

  return group;
}

/**
 * Generate a patch of Mapbox layout properties, so we can update the text layout property based on
 * the parameter unit of the MetX layer description.
 */

export function unitLabelLayout(props: Pick<PoiLayer, "parameter_unit">): SymbolLayout {
  const paramUnit = props.parameter_unit; // e.g: t_2m:C

  if (!paramUnit) {
    // Only render the value if no param unit is defined somehow.
    return { "text-field": ["get", "value"] };
  }

  if (paramUnit?.startsWith(WEATHER_SYMBOL_PARAM)) {
    return { "text-field": "" };
  }

  const unitStr = tryPrettyPrintUnit(paramUnit);
  return {
    // This will render "<value><unitStr>" on the Mapbox symbol layer.
    "text-field": ["concat", ["get", "value"], unitStr],
    "text-size": ["*", ["get", "item-text-size"], 0.5], // using the default icon size for the box size
  };
}

/**
 * Generate a patch for Mapbox paint properties for changing label background opacity
 *
 * @param props PoiLayer definition
 * @returns
 */
const _offsetAbove = [0, -2.1];
const _noOffset = [0, 0];

/**
 * Controls the text offset of the value label pill based on the existence of the icon or text on POI.
 * @param props
 * @returns
 */
export function valueLayerOffset(props: PoiLayer): SymbolLayout {
  const valueShouldOffset = props.poiOptions.item.icon !== "" || props.poiOptions.item.text !== "";
  return {
    "text-offset": valueShouldOffset ? _offsetAbove : _noOffset,
  };
}
